import type { BudgetCategory, BudgetItem } from "./types";

/** Movie Magic–style account numbers: digits with optional hyphen segments (e.g. 59-01). */
export const BUDGET_ACCOUNT_NUMBER_PATTERN = /^[0-9]+(-[0-9]+)*$/;

export function normalizeBudgetAccountNumber(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function getBudgetAccountNumberFormatError(accountNumber: string): string | null {
  const normalized = normalizeBudgetAccountNumber(accountNumber);
  if (!normalized) return null;

  if (!BUDGET_ACCOUNT_NUMBER_PATTERN.test(normalized)) {
    return "Account number must use digits and optional hyphen segments (e.g. 59-01).";
  }

  return null;
}

function collectItemAccountNumbers(items: BudgetItem[], excludeItemId?: number): string[] {
  const numbers: string[] = [];

  for (const item of items) {
    if (excludeItemId !== item.budget_item_id) {
      const normalized = normalizeBudgetAccountNumber(item.account_number);
      if (normalized) numbers.push(normalized);
    }

    if (item.children?.length) {
      numbers.push(...collectItemAccountNumbers(item.children, excludeItemId));
    }
  }

  return numbers;
}

export function collectBudgetAccountNumbers(
  categories: BudgetCategory[],
  options?: {
    excludeItemId?: number;
    excludeSubCategoryId?: number;
    excludeCategoryId?: number;
  }
): string[] {
  const numbers: string[] = [];

  for (const category of categories) {
    if (options?.excludeCategoryId === category.budget_category_id) continue;

    const fringeNumber = normalizeBudgetAccountNumber(category.fringe_account_number);
    if (fringeNumber) numbers.push(fringeNumber);

    for (const subCategory of category.sub_categories) {
      if (options?.excludeSubCategoryId === subCategory.budget_sub_category_id) continue;

      const subCategoryNumber = normalizeBudgetAccountNumber(subCategory.account_number);
      if (subCategoryNumber) numbers.push(subCategoryNumber);

      numbers.push(...collectItemAccountNumbers(subCategory.items, options?.excludeItemId));
    }
  }

  return numbers;
}

export function getBudgetAccountNumberDuplicateError(
  categories: BudgetCategory[],
  accountNumber: string,
  excludeItemId: number
): string | null {
  const normalized = normalizeBudgetAccountNumber(accountNumber);
  if (!normalized) return null;

  const existing = new Set(collectBudgetAccountNumbers(categories, { excludeItemId }));
  if (existing.has(normalized)) {
    return `Account numbers must be unique within the budget. "${normalized}" is already in use.`;
  }

  return null;
}
